import React, { useEffect, useState } from "react";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import useDebounce from "src/hooks/useDebounce";

import AirbyteApi from "apis/airbyte";
import { Toastr } from "clearsense-ui/components";
import { useAirbyteDispatch, useAirbyteState } from "contexts/airbyte";
import { useUserState } from "contexts/user";
import { updateDurationBaseStrings } from "utils/date";

import NewConnection from "./Add";
import EditConnection from "./Edit";
import FilterHeader from "./FilterHeader";
import MainHeader from "./MainHeader";
import MultiDeleteAlert from "./MultiDeleteAlert";
import ResetAlert from "./ResetAlert";
import DBTable from "./Table";
import { getConnectionsData } from "./Table/data";

dayjs.extend(relativeTime);

const SourceDatabases = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [isNewConnectionOpen, setIsNewConnectionOpen] = useState(false);
  const [isResetAlertOpen, setIsResetAlertOpen] = useState(false);
  const [isMultiDeleteAlertOpen, setIsMultiDeleteAlertOpen] = useState(false);

  const user = useUserState();
  const airbyteState = useAirbyteState();
  const airbyteDispatch = useAirbyteDispatch();
  const debouncedSearchString = useDebounce(searchString, 1000);

  const setWorkspaceId = workspaceId =>
    airbyteDispatch?.({
      type: "SET_WORKSPACE_ID",
      payload: { workspaceId },
    });

  const completeInitialSetup = async workspaceId => {
    try {
      const payload = {
        workspaceId,
        email: user.email,
        initialSetupComplete: true,
        displaySetupWizard: true,
        anonymousDataCollection: false,
        news: false,
        securityUpdates: false,
        notifications: [],
      };
      await AirbyteApi.updateWorkspace(payload);
    } catch {
      logger.error("error");
    }
  };

  const fetchWorkspaceId = async () => {
    try {
      setIsLoading(true);
      updateDurationBaseStrings();
      const {
        data: { workspaces },
      } = await AirbyteApi.fetchWorkspaces();
      const [{ workspaceId, initialSetupComplete }] = workspaces;
      if (!initialSetupComplete) await completeInitialSetup(workspaceId);
      setWorkspaceId(workspaceId);
      fetchConnections(workspaceId);
    } catch (error) {
      logger.error(error);
      setIsLoading(false);
    }
  };

  const getFilteredConnections = connections =>
    connections.filter(
      connection =>
        connection.source.name
          .toUpperCase()
          .includes(debouncedSearchString.toUpperCase()) ||
        connection.destination.name
          .toUpperCase()
          .includes(debouncedSearchString.toUpperCase())
    );

  const fetchConnections = async workspaceId => {
    try {
      setIsLoading(true);
      const { data } = await AirbyteApi.fetchConnections({ workspaceId });
      const filteredConnections = debouncedSearchString
        ? getFilteredConnections(data.connections)
        : data.connections;
      const connectionsData = await getConnectionsData(filteredConnections);
      setConnections(connectionsData);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const connectionStatusChanged = newConnections => {
    const currentConnectionsNormalized = connections.map(conn => ({
      id: conn.key,
      isSyncing: conn.isSyncing,
      status: conn.syncStatus,
    }));
    const newConnectionsNormalized = newConnections.map(conn => ({
      id: conn.connectionId,
      isSyncing: conn.isSyncing,
      status: conn.latestSyncJobStatus || "pending",
    }));

    return (
      JSON.stringify(currentConnectionsNormalized) !==
      JSON.stringify(newConnectionsNormalized)
    );
  };

  const pollConnections = async workspaceId => {
    try {
      const { data } = await AirbyteApi.fetchConnections({ workspaceId });
      const filteredConnections = debouncedSearchString
        ? getFilteredConnections(data.connections)
        : data.connections;

      if (connectionStatusChanged(filteredConnections)) {
        const connectionsData = await getConnectionsData(filteredConnections);
        setConnections(connectionsData);
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const handleMultiDelete = async () => {
    try {
      setIsLoading(true);
      await Promise.all(
        selectedRows.map(connectionId =>
          AirbyteApi.deleteConnection({ connectionId })
        )
      );
      Toastr.success("Connections deleted successfully");
      setSelectedRows([]);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
      setIsMultiDeleteAlertOpen(false);
      fetchConnections(airbyteState.workspaceId);
    }
  };

  useEffect(() => {
    if (!isNewConnectionOpen && !selectedConnection) fetchWorkspaceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewConnectionOpen, selectedConnection, debouncedSearchString]);

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <MainHeader openNewConnectionPane={() => setIsNewConnectionOpen(true)} />
      <FilterHeader
        connectionCount={connections.length}
        searchString={searchString}
        setSearchString={setSearchString}
        isDeleteButtonDisabled={selectedRows.length === 0}
        openDeleteAlert={() => setIsMultiDeleteAlertOpen(true)}
      />
      <DBTable
        setSelectedRows={setSelectedRows}
        connections={connections}
        isLoading={isLoading}
        pollConnections={pollConnections}
        createConnection={() => setIsNewConnectionOpen(true)}
        editConnection={conn => setSelectedConnection(conn)}
        debouncedSearchString={debouncedSearchString}
      />

      <NewConnection
        isOpen={isNewConnectionOpen}
        close={() => setIsNewConnectionOpen(false)}
      />
      <EditConnection
        close={() => setSelectedConnection()}
        connection={selectedConnection}
        fetchConnections={fetchConnections}
      />

      <ResetAlert
        isOpen={isResetAlertOpen}
        close={() => setIsResetAlertOpen(false)}
      />
      <MultiDeleteAlert
        isOpen={isMultiDeleteAlertOpen}
        close={() => setIsMultiDeleteAlertOpen(false)}
        rowCount={selectedRows.length}
        submit={handleMultiDelete}
      />
    </div>
  );
};

export default SourceDatabases;
